import {useEffect, useRef, useState} from "react";
import {connect, useDispatch} from "react-redux";
import {Navigate, useNavigate} from "react-router-dom";

import UpImage from "../../../images/image-upload.svg";
import verifImage from "../../../images/Verif.svg";
import {useDeleteFile, useAddFile} from "../../../hooks/useFile";
import {useMe} from "../../../hooks/useGetUser";
import {Logout, verifAction} from "../../../store/actions/AuthActions";
import {
  getUser,
  isAuthenticated,
  isLoading as selectIsLoading,
} from "../../../store/selectors/AuthSelectors";
import {
  accountStatus,
  documentType,
  fileTypeRelation,
} from "../../../utils/constants";
import {
  DOCUMENT_ACCEPT_ATTRIBUTE,
  getUploadErrorMessage,
  isPdfDocument,
  validateDocumentFile,
} from "../../../utils/documentUpload";
import {buildImageUrl} from "../../../utils/imageUrlBuilder";
import {fetchPrivateDocumentUrl} from "../../../services/privateDocumentService";
import {rejectReasons} from "../../../utils/status";
import {identityTypesToClear} from "../../../utils/identityDocuments";
import DocumentPreview from "../../components/shared/DocumentPreview";
import Loader from "../../components/shared/Loader";
import {showToastModal} from "../../components/shared/ToastModalSwal";

const EMPTY_IDENTITY = {
  [documentType.CNI_UP]: null,
  [documentType.CNI_DOWN]: null,
  [documentType.PASSPORT]: null,
};

const IDENTITY_LABELS = {
  [documentType.CNI_UP]: "CNI recto",
  [documentType.CNI_DOWN]: "CNI verso",
  [documentType.PASSPORT]: "Passeport",
};

const toDocumentEntry = async (upload, privateUrls) => {
  const isIdentity = Object.hasOwn(EMPTY_IDENTITY, upload.type);
  let filePath = buildImageUrl(upload.path);
  if (isIdentity && upload.id) {
    try {
      filePath = await fetchPrivateDocumentUrl(upload.id);
      if (!privateUrls.active) {
        URL.revokeObjectURL(filePath);
        return {...upload, filePath: "", isPdf: isPdfDocument(upload), persisted: true};
      }
      privateUrls.push(filePath);
    } catch (_) {
      filePath = "";
    }
  }
  return {...upload, filePath, isPdf: isPdfDocument(upload), persisted: true};
};

const VerifPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const diplomaInputRef = useRef();
  const {data, isLoading, isError} = useMe();
  const {onAdd} = useAddFile();
  const {onDelete} = useDeleteFile();

  const [selectedDocument, setSelectedDocument] = useState("cni");
  const [identityDocuments, setIdentityDocuments] = useState(EMPTY_IDENTITY);
  const [diplomas, setDiplomas] = useState([]);
  const [pendingUploads, setPendingUploads] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    const uploads = data?.Technician?.FileUploads;
    if (!uploads) return;
    let active = true;
    const privateUrls = [];
    privateUrls.active = true;
    const loadDocuments = async () => {
      const nextIdentity = {...EMPTY_IDENTITY};
      const nextDiplomas = [];
      for (const upload of uploads) {
        const entry = await toDocumentEntry(upload, privateUrls);
        if (upload.type === documentType.DIPLOMA) nextDiplomas.push(entry);
        else if (Object.hasOwn(nextIdentity, upload.type)) nextIdentity[upload.type] = entry;
      }
      if (!active) return;
      setIdentityDocuments(nextIdentity);
      setDiplomas(nextDiplomas);
      if (
        nextIdentity[documentType.PASSPORT] &&
        !nextIdentity[documentType.CNI_UP] &&
        !nextIdentity[documentType.CNI_DOWN]
      ) setSelectedDocument("passport");
    };
    loadDocuments();
    return () => {
      active = false;
      privateUrls.active = false;
      privateUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [data]);

  if (props.isLoading || isLoading) return <Loader />;
  if (!props.isAuthenticated || isError) {
    return <Navigate to="/login" replace />;
  }

  const technicianId = data?.Technician?.id || props.user?.Technician?.id;

  const showFileError = (message) =>
    showToastModal({
      success: false,
      title: "Fichier non accepté",
      message,
      duration: 6500,
    });

  const createUpload = (file, type) => {
    const validationError = validateDocumentFile(file);
    if (validationError) {
      showFileError(validationError);
      return null;
    }

    const formData = new FormData();
    formData.append("fileUpload", file);
    formData.append("type", type);
    formData.append("fileUploadId", technicianId);
    formData.append("fileUploadType", fileTypeRelation.technician);

    return {
      formData,
      entry: {
        filePath: URL.createObjectURL(file),
        name: file.name,
        isPdf: isPdfDocument(file),
        persisted: false,
      },
    };
  };

  const selectIdentityFile = (file, type) => {
    if (!file) return;
    const upload = createUpload(file, type);
    if (!upload) return;

    const previous = identityDocuments[type];
    if (previous?.persisted && previous.id) {
      setPendingDeletes((current) => [...new Set([...current, previous.id])]);
    }
    setIdentityDocuments((current) => ({...current, [type]: upload.entry}));
    setPendingUploads((current) => ({...current, [type]: upload.formData}));
  };

  const selectDiploma = (file) => {
    if (!file) return;
    const upload = createUpload(file, documentType.DIPLOMA);
    if (!upload) return;

    const localId = upload.entry.filePath;
    setDiplomas((current) => [...current, {...upload.entry, localId}]);
    setPendingUploads((current) => ({...current, [localId]: upload.formData}));
  };

  const removeIdentity = (type) => {
    const document = identityDocuments[type];
    if (document?.persisted && document.id) {
      setPendingDeletes((current) => [...new Set([...current, document.id])]);
    }
    setPendingUploads((current) => {
      const next = {...current};
      delete next[type];
      return next;
    });
    setIdentityDocuments((current) => ({...current, [type]: null}));
  };

  const changeIdentityType = (selection) => {
    if (selection === selectedDocument) return;
    const obsoleteTypes = identityTypesToClear(selection, documentType);
    const obsoleteIds = obsoleteTypes
      .map((type) => identityDocuments[type])
      .filter((document) => document?.persisted && document.id)
      .map((document) => document.id);

    if (obsoleteIds.length) {
      setPendingDeletes((current) => [...new Set([...current, ...obsoleteIds])]);
    }
    setPendingUploads((current) => {
      const next = {...current};
      obsoleteTypes.forEach((type) => delete next[type]);
      return next;
    });
    setIdentityDocuments((current) => {
      const next = {...current};
      obsoleteTypes.forEach((type) => {
        next[type] = null;
      });
      return next;
    });
    setSelectedDocument(selection);
  };

  const removeDiploma = (document) => {
    if (document.persisted && document.id) {
      setPendingDeletes((current) => [...new Set([...current, document.id])]);
    }
    if (document.localId) {
      setPendingUploads((current) => {
        const next = {...current};
        delete next[document.localId];
        return next;
      });
    }
    setDiplomas((current) => current.filter((item) => item !== document));
  };

  const hasRequiredIdentity =
    selectedDocument === "passport"
      ? Boolean(identityDocuments[documentType.PASSPORT])
      : Boolean(
          identityDocuments[documentType.CNI_UP] &&
            identityDocuments[documentType.CNI_DOWN],
        );

  const submitDocuments = async () => {
    if (!hasRequiredIdentity) {
      showToastModal({
        success: false,
        title: "Pièce d’identité incomplète",
        message:
          selectedDocument === "passport"
            ? "Ajoutez une image ou un PDF lisible de votre passeport."
            : "Ajoutez le recto et le verso de votre carte d’identité.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Replacements must be deleted before uploading the new document.
      // Sequential transfers are more reliable on mobile networks and avoid
      // racing two identity sides against the same profile.
      for (const id of pendingDeletes) {
        await onDelete({id});
      }
      for (const [key, formData] of Object.entries(pendingUploads)) {
        await onAdd(formData, ({loaded, total}) =>
          setUploadProgress((current) => ({
            ...current,
            [key]: total ? Math.round((loaded / total) * 100) : 0,
          })),
        );
      }

      await dispatch(
        verifAction(
          {
            id: technicianId,
            body: {status: accountStatus.VALIDATION_WAITING},
          },
          navigate,
        ),
      );

      await showToastModal({
        success: true,
        title: "Documents envoyés",
        message:
          "Vos documents ont bien été transmis. Votre compte va maintenant être vérifié.",
        duration: 3000,
      });
    } catch (error) {
      showToastModal({
        success: false,
        title: "Importation impossible",
        message: getUploadErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const logout = () => dispatch(Logout(navigate));
  const status = props.user?.Technician?.status;

  if (status === accountStatus.VALIDATION_WAITING) {
    return (
      <div className="flex min-h-[70vh] w-full items-center justify-center px-4">
        <div className="card w-full max-w-3xl overflow-hidden rounded-3xl border-0 shadow-sm">
          <div className="card-body flex flex-col items-center gap-5 px-6 py-12 text-center sm:px-12">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#EAF2FF]">
              <img src={verifImage} alt="Vérification en cours" className="h-14 w-14" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#19295C] sm:text-3xl">
                Vérification de votre compte en cours
              </h1>
              <p className="mx-auto mt-3 max-w-xl text-base leading-7 text-[#667085]">
                Notre équipe examine vos informations et vos justificatifs. Le délai
                moyen est de 24 heures. Vous recevrez une notification dès que votre
                compte sera validé ou si un document complémentaire est nécessaire.
              </p>
            </div>
            <div className="rounded-xl bg-[#FFF5ED] px-5 py-3 text-sm text-[#8A3B00]">
              Aucune action supplémentaire n’est nécessaire pour le moment.
            </div>
            <button
              type="button"
              onClick={logout}
              className="rounded-lg border border-[#0D44B1] bg-white px-6 py-3 text-sm font-semibold uppercase text-[#0D44B1]"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  const identityTypes =
    selectedDocument === "passport"
      ? [documentType.PASSPORT]
      : [documentType.CNI_UP, documentType.CNI_DOWN];

  return (
    <div className="flex w-full justify-center px-4 py-6">
      <div className="card w-full max-w-5xl overflow-hidden rounded-3xl">
        <div className="card-header flex flex-col gap-1 border-b px-6 py-5 sm:px-10">
          <h1 className="text-2xl font-bold text-[#19295C]">
            {status === accountStatus.DISALLOW
              ? "Complétez les documents demandés"
              : "Finalisez votre inscription"}
          </h1>
          {status === accountStatus.DISALLOW && (
            <p className="text-sm text-[#B42318]">
              Motif : {rejectReasons[props.user?.Technician?.rejectionReason] || "Document à corriger"}
            </p>
          )}
        </div>

        <div className="card-body flex flex-col gap-8 px-6 py-7 sm:px-10">
          <section className="flex flex-col gap-4">
            <div>
              <h2 className="text-xl font-semibold text-[#19295C]">
                Pièce d’identité <span className="text-[#D92D20]">*</span>
              </h2>
              <p className="mt-1 text-sm text-[#667085]">
                Ajoutez 1 fichier par emplacement. Formats JPG, PNG, WEBP ou PDF,
                10 Mo maximum par fichier. Cliquez sur un document pour le visualiser.
              </p>
            </div>

            <div className="flex gap-5">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="identityType"
                  checked={selectedDocument === "cni"}
                  onChange={() => changeIdentityType("cni")}
                />
                Carte d’identité
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="identityType"
                  checked={selectedDocument === "passport"}
                  onChange={() => changeIdentityType("passport")}
                />
                Passeport
              </label>
            </div>

            <div className="flex flex-wrap gap-5">
              {identityTypes.map((type) => {
                const document = identityDocuments[type];
                return document ? (
                  <DocumentPreview
                    key={type}
                    document={document}
                    label={IDENTITY_LABELS[type]}
                    onDelete={() => removeIdentity(type)}
                    uploading={isSubmitting && Boolean(pendingUploads[type])}
                    progress={uploadProgress[type] || 0}
                  />
                ) : (
                  <label
                    key={type}
                    className="relative flex h-[160px] w-[200px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#1A64B9] bg-white"
                  >
                    <input
                      type="file"
                      accept={DOCUMENT_ACCEPT_ATTRIBUTE}
                      className="absolute inset-0 cursor-pointer opacity-0"
                      onChange={(event) => {
                        selectIdentityFile(event.target.files?.[0], type);
                        event.target.value = "";
                      }}
                    />
                    <img src={UpImage} alt="" className="h-12 w-12" />
                    <span className="mt-2 text-sm font-medium text-[#1A64B9]">
                      {IDENTITY_LABELS[type]}
                    </span>
                    <span className="mt-1 text-[11px] text-[#667085]">Image ou PDF · 10 Mo max</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="flex flex-col gap-4 border-t border-[#EAECF0] pt-7">
            <div>
              <h2 className="text-xl font-semibold text-[#19295C]">
                Diplômes et certifications <span className="font-normal text-[#667085]">(facultatif)</span>
              </h2>
              <p className="mt-1 text-sm text-[#667085]">
                Vous pourrez aussi les ajouter plus tard depuis votre profil.
                Ajoutez 1 fichier à la fois, 10 Mo maximum par fichier.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <button
                type="button"
                onClick={() => diplomaInputRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  selectDiploma(event.dataTransfer.files?.[0]);
                }}
                className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#1A64B9] bg-white px-4 py-5"
              >
                <img src={UpImage} alt="" className="h-14 w-14" />
                <span className="mt-2 font-semibold text-[#19295C]">
                  Glissez un fichier ici ou cliquez pour parcourir
                </span>
                <span className="mt-1 text-xs text-[#667085]">Image ou PDF · 10 Mo max</span>
              </button>
              <input
                ref={diplomaInputRef}
                type="file"
                accept={DOCUMENT_ACCEPT_ATTRIBUTE}
                className="hidden"
                onChange={(event) => {
                  selectDiploma(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {diplomas.map((document) => (
                  <DocumentPreview
                    key={document.id || document.localId || document.filePath}
                    document={document}
                    label={document.name || "Diplôme ou certification"}
                    onDelete={() => removeDiploma(document)}
                    className="!h-auto !w-full aspect-square"
                    uploading={isSubmitting && Boolean(pendingUploads[document.localId])}
                    progress={uploadProgress[document.localId] || 0}
                  />
                ))}
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={logout}
              className="rounded-lg border border-[#0D44B1] bg-white py-3 text-sm font-semibold uppercase text-[#0D44B1]"
            >
              Se déconnecter
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={submitDocuments}
              className="rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] py-3 text-sm font-semibold uppercase text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Envoi en cours…" : "Envoyer pour vérification"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticated(state),
  isLoading: selectIsLoading(state),
  user: getUser(state),
});

export default connect(mapStateToProps)(VerifPage);
